import React, {ReactElement} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSectionTable} from "../../containers/MainSection/MainSection"
import ManagedSolutionsUsersTable from "../../components/ManagedSolutionsUsers/ManagedSolutionsUsersTable"
import {usePaging} from "../../hooks/usePaging"
import {useAccessToken} from "../../services/auth"
import {findManagedSolutionsUsers, exportManagedSolutionsUsers} from "../../resources/managedSolutions"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {DataTableActionHeader, DataTableCard, TablePending} from "../../components/DataTable/DataTable"
import {useQueryState} from "use-location-state"
import {Filter} from "../../components/Filter/Filter"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"

type UserRoleFilter = "Owner" | "Admin" | "ReadOnly" | "Cardholder" | ""

const userRoleOptions = new Map<UserRoleFilter, string>([
	["Owner", "Owner"],
	["Admin", "Admin"],
	["ReadOnly", "Read Only"],
	["Cardholder", "Cardholder"],
	["", "All"],
])

export function ManagedSolutionsUsersPage(): ReactElement {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const limit = 40

	const [filteredRole, setFilteredRole] = useQueryState<UserRoleFilter>("rtl-user-filter[userRole]", "Owner")
	const roleSelections: UserRoleFilter[] = filteredRole === "" ? [] : [filteredRole]

	const [sortBy, setSortBy] = useQueryState("rtl-user--sort", "-createdAt")

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => findManagedSolutionsUsers(accessToken, offset, limit, filteredRole || undefined, sortBy),
		(x) => x.data.length,
		[filteredRole, sortBy],
		"rtl-user",
		true
	)

	const handleRoleChange = (next: UserRoleFilter[]) => {
		setFilteredRole(next.length === 0 ? "" : next[next.length - 1])
		reset(limit)
	}

	const toggleSort = () => {
		setSortBy(sortBy === "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportUsers() {
		const response = await exportManagedSolutionsUsers(accessToken, filteredRole || undefined, sortBy)
		response.match(
			(d) => downloadFile("ManagedSolutionsUsers.csv", d, "text/csv"),
			() => addToast("Something went wrong", {appearance: "warning"})
		)
		return true
	}

	const roleFilter = (
		<Filter title="Role" setStatuses={handleRoleChange} statuses={roleSelections} options={userRoleOptions} />
	)

	return (
		<>
			<TitleBar title={"Users"} />

			<MainSectionTable>
				<DataTableCard className={"managed-solutions-users-card"}>
					<DataTableActionHeader enableSticky={true} filters={[roleFilter]} exportFunc={exportUsers} />
					<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending numberOfRows={2} />}>
						{({value: users}) => {
							return (
								<ManagedSolutionsUsersTable
									users={users.data}
									hasResults={hasResults}
									hasPrev={hasPrev}
									hasNext={hasNext}
									prev={prev}
									next={next}
									isUsingPaging={true}
									fullHeight={true}
									meta={users.meta}
									sortBy={sortBy}
									sortFunction={toggleSort}
								/>
							)
						}}
					</AsyncResultComponent>
				</DataTableCard>
			</MainSectionTable>
		</>
	)
}
